import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import { type IProject, Project } from '@app/land-hold/project/models/Project';
import type { FormOptions } from '@shared/types/Form';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';
import { route } from 'ziggy-js';
import type { ListResponse } from '@shared/types/ApiResponse';

export function useProjectRepository<T extends IProject = IProject>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                Project.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Project.routes().update, {
                        id,
                    }),
                    method: Project.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) =>
                    usePayload<T>(data, {
                        exclude: ['steward', 'properties_count', 'properties', 'approvalAuthority'],
                        nullable: ['sent_for_approval_at', 'end_date'],
                    }),
                ).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(Project.routes().destroy, {
                        id,
                    }),
                    method: Project.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(Project.routes().show, { id }) };
        };

        const list = () => {
            const { data: projects, processing: fetchingProjects } = useFetchListData<Project>(useRoute().build(Project.routes().index), 'appSection@project::IndexProjectsPage', (payload) => Project.from(payload));
            return { projects, fetchingProjects };
        };

        const listAllowedFor = (stewardId: string) => {
            const data = ref<IProject[]>([]);
            const processing = ref(true);

            window.axios
                .get<ListResponse<IProject>>(route('stewards.eligible-projects', { id: stewardId }))
                .then((response) => {
                    data.value = response.data.data;
                })
                .catch((error) => {
                    console.error('Failed to load eligible projects for steward', error);
                })
                .finally(() => {
                    processing.value = false;
                });

            return { data, processing };
        };

        return {
            show,
            list,
            listAllowedFor,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
